import styled from "styled-components";
import React, { useState } from "react";

// --- INÍCIO DOS STYLED COMPONENTS ---
const UserListBoxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const ListBoxHeader = styled.h4`
    font-weight: 600;
    color: #4a5568;
    & span {
        font-size: 0.875rem;
        font-weight: 400;
        color: #718096;
    }
`;

const ListBox = styled.div`
    border: 1px solid ${props => props.isOver ? '#14a2b8' : '#D1D5DB'};
    box-shadow: ${props => props.isOver ? '0 0 0 2px #BFDBFE' : 'none'};
    border-radius: 0.5rem;
    background-color: #fff;
    height: 20rem;
    overflow-y: auto;
    transition: all 0.2s;
`;

const UserItemContainer = styled.div`
    display: flex;
    align-items: center;
    padding: 0.75rem;
    cursor: pointer;
    transition: background-color 0.15s;
    background-color: ${props => props.selected ? '#a6e0e9ff' : 'transparent'};
    
    &:hover {
        background-color: ${props => props.selected ? '#a6e0e9ff' : '#F9FAFB'};
    }
`;

const Avatar = styled.div`
    flex-shrink: 0;
    height: 2rem;
    width: 2rem;
    border-radius: 9999px;
    background-color: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.75rem;

    & span {
        font-size: 0.75rem;
        font-weight: bold;
        color: #4b5563;
    }
`;

const UserInfo = styled.div`
    p {
        margin: 0;
    }
    p:first-child {
        font-weight: 500;
        font-size: 0.875rem;
        color: #1f2937;
    }
    p:last-child {
        font-size: 0.75rem;
        color: #6b7280;
    }
`;

const EmptyListMessage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 0.875rem;
    color: #9ca3af;
    padding: 1rem;
    text-align: center;
`;

const ActionButtons = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
`;

const IconButton = styled.button`
    padding: 0.5rem;
    border-radius: 9999px;
    background-color: #e5e7eb;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #d1d5db;
    }

    & svg {
        height: 1rem;
        width: 1rem;
        color: #4b5563;
    }
`;
// --- FIM DOS STYLED COMPONENTS (UserListBox) ---

/**
 * @component UserListBox
 * @brief Renderiza uma das caixas (Disponíveis ou Selecionados) da interface
 * * Gerencia a seleção interna (cliques), o estado de "hover" do drag-and-drop,
 * e chama as funções de `onMove`, `onMoveAll` ou `onDrop` passadas pelo componente pai
 * * @param {string} title - O título da caixa (ex: "Usuários Disponíveis")
 * @param {Array<Object>} users - A lista de usuários a ser exibida
 * @param {Function} onMove - Callback para mover usuários selecionados (via botão)
 * @param {Function} onMoveAll - Callback para mover todos os usuários (via botão)
 * @param {Function} onDrop - Callback para mover um usuário (via drag-and-drop)
 * @param {boolean} [isDestination=false] - Flag para lógica interna (não usada no momento, mas pode ser útil)
 */
function UserListBox({
    title,
    users,
    onMove,
    onMoveAll,
    onDrop,
    isDestination = false,
}) {
    // Estado para controlar quais usuários estão selecionados (para mover em massa)
    const [selected, setSelected] = useState(new Set());
    // Estado para controlar o feedback visual do drag-and-drop (borda azul)
    const [isOver, setIsOver] = useState(false);

    /**
     * @function handleDragStart
     * @brief Armazena o ID do usuário que está sendo arrastado
     */
    const handleDragStart = (e, userId) =>
        e.dataTransfer.setData("userId", userId);

    /**
     * @function handleDragOver
     * @brief Permite que a caixa receba um item (necessário para o `onDrop`)
     */
    const handleDragOver = (e) => e.preventDefault();

    /**
     * @function handleDrop
     * @brief Lê o ID do usuário arrastado e chama a callback `onDrop` do componente pai
     */
    const handleDrop = (e) => {
        e.preventDefault();
        const userId = parseInt(e.dataTransfer.getData("userId"), 10);
        if (userId) onDrop(userId);
        setIsOver(false);
    };

    /**
     * @function toggleSelection
     * @brief Adiciona ou remove um usuário do estado `selected` (para seleção múltipla)
     */
    const toggleSelection = (userId) => {
        setSelected((prev) => {
            const newSet = new Set(prev);
            newSet.has(userId) ? newSet.delete(userId) : newSet.add(userId);
            return newSet;
        });
    };

    /**
     * @function handleMoveClick
     * @brief Chama a callback `onMove` do pai com todos os usuários selecionados
     */
    const handleMoveClick = () => {
        if (selected.size > 0) {
            onMove(Array.from(selected));
            setSelected(new Set()); // Limpa a seleção após mover
        }
    };
    // Define qual ícone de seta usar (esquerda ou direita)
    const directionIcon = isDestination ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7";
    const directionIconAll = isDestination
        ? "M11 19l-7-7 7-7m8 14l-7-7 7-7"
        : "M13 5l7 7-7 7M5 5l7 7-7 7";

    return (
        <UserListBoxContainer>
            <ListBoxHeader>
                {title} <span>({users.length})</span>
            </ListBoxHeader>
            {/* A caixa de listagem que responde aos eventos de drag-and-drop */}
            <ListBox
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnter={() => setIsOver(true)}
                onDragLeave={() => setIsOver(false)}
                isOver={isOver}
            >
                {users.length > 0 ? (
                    users.map((user) => (
                        // Cada item de usuário individual
                        <UserItemContainer
                            key={user.id}
                            draggable="true"
                            onDragStart={(e) => handleDragStart(e, user.id)}
                            onClick={() => toggleSelection(user.id)}
                            selected={selected.has(user.id)}
                        >
                            <Avatar>
                                <span>
                                    {user.nome
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")
                                        .slice(0, 2)
                                        .toUpperCase()}
                                </span>
                            </Avatar>
                            <UserInfo>
                                <p>{user.nome}</p>
                                <p>{user.email}</p>
                            </UserInfo>
                        </UserItemContainer>
                    ))
                ) : (
                    // Mensagem exibida quando a lista está vazia
                    <EmptyListMessage>
                        {isDestination
                            ? "Nenhum usuário selecionado."
                            : "Todos os usuários já foram selecionados."}
                    </EmptyListMessage>
                )}
            </ListBox>
            {/* Botões de ação (Mover selecionados / Mover todos) */}
            <ActionButtons>
                <IconButton
                    onClick={handleMoveClick}
                    title={
                        isDestination
                            ? "Remover selecionados"
                            : "Adicionar selecionados"
                    }
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d={directionIcon}
                        />
                    </svg>
                </IconButton>
                <IconButton
                    onClick={onMoveAll}
                    title={isDestination ? "Remover todos" : "Adicionar todos"}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d={directionIconAll}
                        />
                    </svg>
                </IconButton>
            </ActionButtons>
        </UserListBoxContainer>
    );
}

export default UserListBox;
